import React, { FC } from "react";
import { useTranslations } from "../localization/Localization";
import { formatDate, formatShortDate } from "../utilities/string";
import { Author, AuthorProps } from "./Author";
import { BreadcrumbProps } from "./Breadcrumb";
import { Breadcrumbs } from "./Breadcrumbs";
import { Columns } from "./containers/Columns";
import { Container } from "./containers/Container";
import { DynamicImage, DynamicImageSource } from "./DynamicImage";
import { Tag } from "./Tag";

export type PageIntroProps = {
  heading: string;
  preamble: string;
  imageUrl?: DynamicImageSource;
  publishDate?: Date;
  lastChangeDate?: Date;
  breadcrumbs: BreadcrumbProps[];
  tags?: string[];
  author?: AuthorProps;
  language?: string;
};

export const PageIntro: FC<PageIntroProps> = ({
  heading,
  preamble,
  imageUrl,
  publishDate,
  lastChangeDate,
  breadcrumbs,
  tags,
  author,
  language = "en"
}) => {
  const translations = useTranslations();

  if (publishDate && lastChangeDate && new Date(publishDate) > new Date(lastChangeDate)) {
    lastChangeDate = publishDate;
  }

  return (
    <Container>
      <Columns colSize="full" desktopColSize="6/12" justifyDesktop="justify-center">
        <div className="mb-xs desktop:mb-md">
          <Breadcrumbs breadcrumbs={breadcrumbs}></Breadcrumbs>
        </div>
      </Columns>
      {!!imageUrl && (
        <Columns colSize="full" desktopColSize="8/12" justifyDesktop="justify-center">
          <DynamicImage src={imageUrl} className="aspect-16/9 object-cover mb-lg" />
        </Columns>
      )}
      <Columns colSize="full" desktopColSize="6/12" justifyDesktop="justify-center">
        <div>
          <div className="flex flex-col">
            {publishDate && (
              <small className="inline-block mb-xs uppercase text-secondary-300 text-stylized tablet:text-stylized">
                {translations.publishDate}{" "}
                <time dateTime={formatShortDate(publishDate)}>
                  {formatDate(publishDate, language)}
                </time>
              </small>
            )}

            {lastChangeDate && (
              <small className="inline-block uppercase text-secondary-300 text-stylized tablet:text-stylized mb-xs">
                {translations.lastChangeDate}{" "}
                <time dateTime={formatShortDate(lastChangeDate)}>
                  {formatDate(lastChangeDate, language)}
                </time>
              </small>
            )}
          </div>
          <h1 className="text-h1 font-serif text-secondary-500 mb-md">{heading}</h1>
          <p className="text-preamble font-serif text-secondary-400">{preamble}</p>
          {!!author && (
            <div className="mt-lg">
              <Author {...author} />
            </div>
          )}
          {!!tags?.length && (
            <div className="flex gap-xxs mt-lg pt-md border-t border-theme3-400">
              {tags.map((tag, key) => (
                <Tag key={key} hasGroupHover>
                  {tag}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </Columns>
    </Container>
  );
};
